import Vue from "vue";
import Swal from "sweetalert2";

const positions = {
  "top-right": "top-end",
  "top-left": "top-start",
  "bottom-right": "bottom-end",
  "bottom-left": "bottom-start",
  top: "top",
  bottom: "bottom",
};

const queue = [];
const queuedByKey = new Map();
let processing = false;

const waitForDialog = resolve => {
  if (!Swal.isVisible()) return resolve();
  window.setTimeout(() => waitForDialog(resolve), 200);
};

const showNext = async () => {
  if (processing || !queue.length) return;
  processing = true;
  await new Promise(waitForDialog);
  const item = queue.shift();
  const { icon, message, options, timer, key } = item;
  const dark = Boolean(document.querySelector(".v-application.theme--dark"));

  const result = await Swal.fire({
    toast: true,
    icon,
    title: message,
    position: positions[options.position] || "top-end",
    timer,
    timerProgressBar: true,
    showConfirmButton: false,
    showCloseButton: true,
    closeButtonAriaLabel: "Dismiss notification",
    customClass: {
      popup: `backoffice-toast${dark ? " backoffice-toast--dark" : ""}`,
      closeButton: "backoffice-toast__close",
    },
    onOpen: popup => {
      popup.setAttribute("role", icon === "error" ? "alert" : "status");
      popup.setAttribute("aria-live", icon === "error" ? "assertive" : "polite");
      popup.addEventListener("mouseenter", Swal.stopTimer);
      popup.addEventListener("mouseleave", Swal.resumeTimer);
      popup.addEventListener("focusin", Swal.stopTimer);
      popup.addEventListener("focusout", Swal.resumeTimer);
    },
  });
  queuedByKey.delete(key);
  if (typeof options.onComplete === "function") options.onComplete();
  item.resolve(result);
  processing = false;
  showNext();
};

const notify = (type, message, options = {}) => {
  const icon = ["success", "error", "info", "warning", "question"].includes(type)
    ? type
    : "info";
  const text = String(message == null ? "" : message);
  const key = `${icon}:${text}`;
  if (queuedByKey.has(key)) return queuedByKey.get(key);
  const duration = Number(options.duration);
  const timer = Number.isFinite(duration) && duration > 0
    ? duration
    : icon === "error" ? 10000 : 3500;
  let resolveNotification;
  const result = new Promise(resolve => { resolveNotification = resolve; });
  queuedByKey.set(key, result);
  if (queue.length >= 5) {
    const dropped = queue.shift();
    queuedByKey.delete(dropped.key);
    dropped.resolve({ dismiss: "queue-limit" });
  }
  queue.push({ icon, message: text, options, timer, key, resolve: resolveNotification });
  window.setTimeout(showNext, 0);
  return result;
};

Vue.prototype.$toasted = {
  success(message, options) { return notify("success", message, options); },
  error(message, options) { return notify("error", message, options); },
  info(message, options) { return notify("info", message, options); },
  show(message, options = {}) { return notify(options.type || "info", message, options); },
};
